import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  isValidEmailForAccess,
  normalizeSuperSoundEmail,
  SUPER_SOUND_ADMIN_EMAIL,
} from "../../constants/superSoundAccess";

export default function SuperSoundAccessPanel({ adminEmail }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [panelError, setPanelError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "superSoundAccess"), orderBy("addedAt", "desc"));
    return onSnapshot(
      q,
      (snap) => {
        setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => setPanelError(err.message || "Could not load access list.")
    );
  }, []);

  const grantAccess = async () => {
    const normalized = normalizeSuperSoundEmail(newEmail);
    if (!isValidEmailForAccess(normalized)) {
      setPanelError("Enter a valid email address.");
      return;
    }
    if (normalized === SUPER_SOUND_ADMIN_EMAIL) {
      setPanelError("The admin account always has access.");
      return;
    }
    setBusy(true);
    setPanelError(null);
    try {
      await setDoc(doc(db, "superSoundAccess", normalized), {
        email: normalized,
        addedAt: serverTimestamp(),
        addedBy: adminEmail,
      });
      setNewEmail("");
    } catch (err) {
      setPanelError(err.message || "Could not add user.");
    } finally {
      setBusy(false);
    }
  };

  const revokeAccess = async (emailDocId) => {
    setBusy(true);
    setPanelError(null);
    try {
      await deleteDoc(doc(db, "superSoundAccess", emailDocId));
    } catch (err) {
      setPanelError(err.message || "Could not remove user.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ss-access-panel" aria-label="Manage library access">
      <h3>{t("sound_manage_access")}</h3>
      <p className="ss-muted">{t("sound_manage_access_hint")}</p>
      <div className="ss-folder-create">
        <input
          type="email"
          placeholder={t("sound_add_user_placeholder")}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && grantAccess()}
          autoComplete="off"
        />
        <button
          type="button"
          className="ss-btn ss-btn-primary"
          onClick={grantAccess}
          disabled={busy}
        >
          {t("sound_add")}
        </button>
      </div>
      {panelError && <p className="ss-access-error" role="alert">{panelError}</p>}
      <ul className="ss-access-list">
        <li className="ss-access-row ss-access-row--admin">
          <span>{SUPER_SOUND_ADMIN_EMAIL}</span>
          <span className="ss-access-badge">{t("sound_admin_badge")}</span>
        </li>
        {members.map((m) => (
          <li key={m.id} className="ss-access-row">
            <span>{m.email || m.id}</span>
            <button
              type="button"
              className="ss-btn ss-btn-ghost ss-access-remove"
              onClick={() => revokeAccess(m.id)}
              disabled={busy}
            >
              {t("sound_remove")}
            </button>
          </li>
        ))}
        {!members.length && (
          <li className="ss-muted">{t("sound_no_extra_users")}</li>
        )}
      </ul>
    </section>
  );
}
